import { Menu, MenuItemProps, Portal } from "@ark-ui/react";
import Icon from "../Icon/Icon";
import { Fragment, useState } from "react";
import { aromaContextualMenu } from "@zenchef/styled-system/recipes";
import {
  ContextualMenuPropsWithChildren,
  ContextualMenuPropsWithItems,
  ContextualMenuPropsWithSections,
  IconItem,
  LabelIconItem,
} from "./types";
import { Button } from "..";
import { cx } from "@zenchef/styled-system/css";
import { Flex } from "@zenchef/styled-system/jsx";

type ContextualMenuItemProps = (LabelIconItem | IconItem) &
  MenuItemProps & {
    onSetValue?: (event: React.MouseEvent<HTMLElement>, value: string) => void;
    className?: string;
  };

const ContextualMenuItem = ({
  icon,
  onSetValue,
  className,
  ...propsWithVariants
}: ContextualMenuItemProps) => {
  const [variants, props] =
    aromaContextualMenu.splitVariantProps(propsWithVariants);
  const classes = aromaContextualMenu(variants);

  const hasLabel = "label" in props;

  return (
    <Menu.Item
      className={cx(classes.item, className)}
      data-icon={!hasLabel}
      onClick={(event) => onSetValue?.(event, props.value)}
      {...props}
    >
      {icon ? <Icon name={icon} /> : null}
      {hasLabel ? props.label : null}
    </Menu.Item>
  );
};

export type ContextualMenuProps =
  | ContextualMenuPropsWithSections
  | ContextualMenuPropsWithItems
  | ContextualMenuPropsWithChildren;

const ContextualMenu = ({
  trigger,
  sections,
  items,
  zIndex,
  children,
  portalled,
  asSubmenu,
  actionLabel,
  secondaryActionLabel,
  menuContentClassName,
  onSetValue,
  onAction,
  onSecondaryAction,
  ...propsWithVariants
}: ContextualMenuProps) => {
  const [variants, props] =
    aromaContextualMenu.splitVariantProps(propsWithVariants);
  const classes = aromaContextualMenu(variants);

  const [open, setOpen] = useState(props.defaultOpen);

  const PortalTag = portalled ? Portal : Fragment;
  const TriggerTag = asSubmenu ? Menu.TriggerItem : Menu.Trigger;

  const hasActionButton = !!(actionLabel && onAction);
  const hasSecondaryActionButton = !!(
    secondaryActionLabel && onSecondaryAction
  );

  const onOpenChange = (details: Menu.OpenChangeDetails) =>
    setOpen(details.open);

  const handleAction = (event: React.MouseEvent<HTMLElement>) => {
    onAction?.(event);
    setOpen(!open);
  };

  const handleSecondaryAction = (event: React.MouseEvent<HTMLElement>) => {
    onSecondaryAction?.(event);
    setOpen(!open);
  };

  return (
    <Menu.Root lazyMount open={open} onOpenChange={onOpenChange} {...props}>
      <TriggerTag asChild type="button">
        {trigger}
      </TriggerTag>
      <PortalTag>
        <Menu.Positioner style={{ zIndex }}>
          <Menu.Content className={cx(classes.content, menuContentClassName)}>
            <Flex
              padding="padding.3"
              flexDir="column"
              gap={hasActionButton ? "gap.1" : undefined}
            >
              {sections !== undefined ? (
                <>
                  {sections.map((section) => (
                    <Menu.ItemGroup key={section.title}>
                      <Menu.ItemGroupLabel>{section.title}</Menu.ItemGroupLabel>
                      {section.items.map((item) => (
                        <ContextualMenuItem
                          key={item.value}
                          onSetValue={onSetValue}
                          {...item}
                        />
                      ))}
                    </Menu.ItemGroup>
                  ))}
                </>
              ) : items !== undefined ? (
                items.map((item) => (
                  <ContextualMenuItem
                    key={item.value}
                    onSetValue={onSetValue}
                    {...item}
                  />
                ))
              ) : (
                children
              )}
            </Flex>
            {hasActionButton ? (
              <Button className={classes.action} onClick={handleAction}>
                {actionLabel}
              </Button>
            ) : null}
            {hasSecondaryActionButton ? (
              <Button
                onClick={handleSecondaryAction}
                hierarchy="secondary-subtler"
                className={classes.secondaryAction}
              >
                {secondaryActionLabel}
              </Button>
            ) : null}
          </Menu.Content>
        </Menu.Positioner>
      </PortalTag>
    </Menu.Root>
  );
};

ContextualMenu.Item = ContextualMenuItem;

export { ContextualMenu };
export default ContextualMenu;
